import {
  format,
  formatDistance,
  formatDistanceStrict,
  formatRelative,
  parse,
  parseISO,
  subDays,
} from 'date-fns';
import { enUS, Locale, ru, uk } from 'date-fns/locale';
import { InterfaceLanguage } from 'types/enums';
import { translate } from './translate';

export default class Formatter {
  static formatDate(
    date: string | Date,
    options?: {
      plusDays?: number;
      timeOptions?: Intl.DateTimeFormatOptions;
      lang?: InterfaceLanguage;
    },
  ) {
    const recievedDate = new Date(date);
    recievedDate.setDate(recievedDate.getDate() + (options?.plusDays || 0));

    return new Intl.DateTimeFormat(options?.lang || 'ru', {
      day: 'numeric',
      month: 'long',
      ...options?.timeOptions,
    }).format(new Date(recievedDate));
  }

  static formatDateRelative(date: string | Date, lang?: InterfaceLanguage) {
    const formatRelativeLocale: { [key: string]: string } = {
      yesterday: 'Вчера в HH:mm',
      today: ' HH:mm',
      lastWeek: 'eeee',
      tomorrow: 'Завтра в HH:mm',
      other: 'dd.MM.yy ', // Difference: Add time to the date
    };

    const locale = {
      ...enUS,
      formatRelative: (token: string) =>
        formatRelativeLocale[token] || formatRelativeLocale['other'],
    };
    if (!date) {
      return '';
    }

    return formatRelative(subDays(new Date(date), 0), new Date(), {
      locale:
        lang === InterfaceLanguage.English
          ? enUS
          : lang === InterfaceLanguage.Ukranian
            ? uk
            : lang === InterfaceLanguage.Russian
              ? ru
              : locale,
    });
  }

  static formatDateRelativeDateFns(date: string | Date, lang?: InterfaceLanguage) {
    if (!date) {
      return '';
    }

    const parsedDate = typeof date === 'string' ? parseISO(date) : date;

    const locales: Record<InterfaceLanguage, Locale> = {
      en: enUS,
      ru: ru,
      ua: uk,
    };

    return formatRelative(parsedDate, new Date(), {
      locale: locales[lang || 'ru'],
    });
  }

  static cleanPhone(phone: string) {
    if (phone) {
      let phoneCleaned = `${phone}`.replace(/[^\+0-9]/g, '');
      if (!phoneCleaned.startsWith('+')) {
        phoneCleaned = '+' + phoneCleaned;
      }
      return phoneCleaned;
    }
    return phone;
  }

  static formatDistanceToNow(
    date: string | Date,
    options?: { plusDistance?: number; strict?: boolean; locale?: InterfaceLanguage },
  ) {
    const recievedDate = new Date(date);
    recievedDate.setDate(recievedDate.getDate() + (options?.plusDistance || 0));
    const dt = options?.strict
      ? formatDistanceStrict(new Date(), new Date(recievedDate), {
          locale: options.locale === 'en' ? enUS : ru,
          roundingMethod: 'round',
        })
      : formatDistance(subDays(new Date(), 0), new Date(recievedDate), { locale: ru });
    return dt;
  }

  /** get formatted phone number +7 999 999 99 99, return +7 999 *** ** 99 */
  static hideSomePhoneNumbers(phone: string) {
    if (!phone) {
      return;
    }
    const phoneResult = phone.split(' ').map((e, index) => {
      return index > 1 && index < 4 ? '*'.repeat(e.length) : e;
    });
    return phoneResult.join(' ');
  }

  static pad(pad: string, str: string, padLeft = true) {
    if (typeof str === 'undefined') return pad;
    if (padLeft) {
      return (pad + str).slice(-pad.length);
    } else {
      return (str + pad).substring(0, pad.length);
    }
  }

  static formatNumber(num: number) {
    return num?.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1 ');
  }

  static formatProductPrice(price: { value?: number; min?: number; max?: number }) {
    const pr = price?.value || price.min;
    if (!pr) {
      return '';
    }
    return this.formatPrice(pr);
  }

  static formatPrice(price: number) {
    if (!price) {
      return;
    }
    return `${this.formatNumber(Math.ceil(price))}`;
  }

  static formatDeliveryPeriod(minPeriod: number, maxPeriod: number) {
    return (
      ((maxPeriod || minPeriod) &&
        (maxPeriod === minPeriod
          ? `${minPeriod} ${translate('day', { count: minPeriod })}`
          : `${minPeriod} - ${maxPeriod} ${translate('day', { count: maxPeriod })}`)) ||
      ''
    );
  }

  static formatBonuses(bonuses: number, withStr = false) {
    const number = bonuses ? Math.ceil(bonuses / 100) : 0;
    return `${this.formatNumber(number)}${withStr ? ` ${translate('mark', { count: number })}` : ''}`;
  }

  static formatAddressSuggestionSubTitle(subTitle: string): string {
    return subTitle
      ?.split(',')
      .map((e) => e.trim())
      .reverse()
      .join(', ');
  }

  static formatDeliveryTime({
    minDeliveryTime,
    maxDeliveryTime,
  }: {
    minDeliveryTime?: number;
    maxDeliveryTime?: number;
  }) {
    return `${minDeliveryTime ?? ''}${minDeliveryTime != null && maxDeliveryTime != null ? '-' : ''}${maxDeliveryTime ?? ''}${minDeliveryTime != null || maxDeliveryTime != null ? ' мин' : ''}`;
  }
  static formatPreOrderTime(date: Date) {
    return Formatter.formatDateRelative(date);
  }

  /** return HH:mm:ss format time */
  static formatSecondsToTime(s: number): string {
    if (!s) {
      return '00:00';
    }
    const date = new Date(0);
    date.setSeconds(s);
    const time = date.toISOString().split('T')[1].split('.')[0].split(':');
    if (time[0] === '00') {
      return `${time[1]}:${time[2]}`;
    }
    return time.join(':');
  }

  /** return dd.MM.yy HH:mm date format */
  static formatToShortYear(date: string | Date) {
    const locale = {
      ...ru,
      formatRelative: () => 'dd.MM.yy HH:mm',
    };
    if (!date) {
      return '';
    }
    const result = formatRelative(
      typeof date === 'string' ? new Date(date) : date,
      new Date(date),
      { locale },
    );
    return result;
  }

  /** get arr like [['02.2022', '03.2023'], ['02.1992', '03.1993']], return for example '2 года 2 месяца' */
  static fullTimeLine = (arr: string[][], short?: boolean) => {
    const allMonths = arr
      .map((el) => {
        const oldString = el[0]?.split('.') || [
          new Date().getMonth().toString(),
          new Date().getFullYear().toString(),
        ];
        const oldDate = new Date(+oldString[1], +oldString[0]);
        const newString = el[1]
          ? el[1]?.split('.')
          : [new Date().getMonth(), new Date().getFullYear()];
        const newDate = new Date(+newString[1], +newString[0]);
        const m1 = oldDate.getMonth() + oldDate.getFullYear() * 12;
        const m2 = newDate.getMonth() + newDate.getFullYear() * 12;
        const result = m2 - m1;
        return result === 0 ? 1 : Math.abs(result);
      })
      .reduce((p, c) => {
        return p + c;
      }, 0);

    const years = ~~(allMonths / 12);
    const months = allMonths % 12;
    const yearsString =
      years > 0 ? years + ' ' + translate(short ? 'year_short' : 'year', { count: years }) : '';
    const monthsString =
      months > 0
        ? months + ' ' + translate(short ? 'month_plural_short' : 'month_plural', { count: months })
        : '';
    return ((yearsString ? yearsString + ' ' : '') + monthsString).trim();
  };

  static getShortDateFromNumeric = (input: string, lang: InterfaceLanguage) => {
    if (!input || input.length === 0) return '';

    const date = parse(input, 'MM.yyyy', new Date());

    const arr = format(date, `LLL yyyy 'r ${' ' + translate('year_short', { count: 1 })}'`, {
      locale: lang === 'en' ? enUS : lang === 'ua' ? uk : ru,
    })
      .toLowerCase()
      .replace(' r ', '')
      .split(' ');

    return `${arr[0]}${lang === InterfaceLanguage.English ? '.' : ''} ${arr.slice(1).join(' ')}`;
  };

  /** get array, return some random elements from it with limit */
  static randomElementsFromArray = <T>(arr: any[], limit: number): T[] => {
    const arrLength = arr.length;
    let newArray: T[] = [];
    while (newArray.length < (arrLength <= limit ? arrLength : limit)) {
      const randomInt = ~~(Math.random() * arrLength);
      newArray.push(arr[randomInt]);
      newArray = newArray.filter((v, i, arr) => arr.indexOf(v) == i);
    }
    return newArray;
  };

  static getMarks = (n: number): (boolean | number)[] => {
    const result: (boolean | number)[] = [];
    for (let i = 0; i < 5; i++) {
      if (n - i < 1 && n % 1 > 0 && i < n) {
        result.push(n % 1);
      } else {
        result.push(i < n);
      }
    }
    return result;
  };

  static kebabCaseToCamelCase = (s: string): string => {
    return s
      .split('-')
      .map((c, idx) => (idx > 0 ? c[0].toUpperCase() + c.slice(1) : c))
      .join('');
  };

  static addSpacesInNumber = (s: string): string => {
    if (typeof s !== 'string') {
      return s;
    }

    return `${s
      .split('')
      .reverse()
      .map((n, idx) => (idx % 3 === 0 ? `${n} ` : n))
      .reverse()
      .join('')}`;
  };
}

export const pad = Formatter.pad;
